import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Stack,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import { useHistory, useLocation } from 'react-router-dom';

import { IssuerType, PlanType, editIssuer, getPlans, validateIpv4List } from '../../Utils/backStageApi';
import { checkAuth } from '../../Utils/common';

const LABEL_STYLE = {
  w: '140px',
  minW: '140px',
  maxW: '140px',
  flexShrink: 0,
  flexGrow: 0,
  whiteSpace: 'nowrap',
  wordBreak: 'keep-all',
  textAlign: 'left',
  fontWeight: 600,
  fontSize: '24px',
  lineHeight: '133%',
} as const;

const INPUT_STYLE = {
  fontFamily: "'Open Sans', sans-serif",
  fontSize: '16px',
  color: '#00292B',
  borderColor: '#A8B8C2',
  borderRadius: '6px',
  _focus: { borderColor: '#00AAFF', boxShadow: '0 0 0 1px #00AAFF' },
  _placeholder: { color: '#A8B8C2' },
};

type FormState = {
  name: string;
  acronym: string;
  ips: string;
  plan_code: string;
  plan_start_date: string;
};

const todayLocal = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const EditIssuerPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const toast = useToast();

  const issuer = (location.state as { issuer: IssuerType } | undefined)?.issuer;

  const initialForm: FormState = {
    name: issuer?.name ?? '',
    acronym: issuer?.acronym ?? '',
    ips: '',
    plan_code: issuer?.plan_code ?? 'starter',
    plan_start_date: issuer?.plan_start_date ?? '',
  };

  const planStarted = !!issuer?.plan_start_date && issuer.plan_start_date <= todayLocal();

  const [form, setForm] = useState<FormState>(initialForm);
  const [plans, setPlans] = useState<PlanType[]>([]);

  const [ipError, setIpError] = useState<string | null>(null);

  const [password, setPassword] = useState({
    text: '',
    visible: false,
    isError: false,
    errorMsg: '密碼錯誤',
  });

  const { isOpen, onOpen, onClose } = useDisclosure();

  useEffect(() => {
    checkAuth();

    if (!issuer) {
      history.goBack();
    }

    getPlans().then((data) => {
      if (!data?.list) return;
      setPlans(data.list);
    });
  }, []);

  if (!issuer) return null;

  const handleIpsChange = (val: string) => {
    setForm((p) => ({
      ...p,
      ips: val,
    }));

    if (val.trim() === '') {
      setIpError(null);
    } else {
      setIpError(validateIpv4List(val));
    }
  };

  const isDirty =
    form.ips.trim() !== '' ||
    form.plan_code !== initialForm.plan_code ||
    form.plan_start_date !== initialForm.plan_start_date;

  const planStartDateError =
    !planStarted && form.plan_start_date.trim() !== '' && form.plan_start_date < todayLocal()
      ? '起始日期不能早於今天'
      : null;

  const isSubmittable = ipError === null && planStartDateError === null;

  const openPasswordModal = () => {
    onOpen();
  };

  const handleModalClose = () => {
    setPassword({
      text: '',
      visible: false,
      isError: false,
      errorMsg: '密碼錯誤',
    });

    onClose();
  };

  const handleUpdate = () => {
    setPassword((prev) => ({
      ...prev,
      isError: false,
    }));

    const payload: Record<string, string> = {
      issuer_id: issuer.id,
    };

    if (form.ips.trim() !== '') {
      payload.ips = form.ips.trim();
    }

    if (form.plan_code !== initialForm.plan_code) {
      payload.plan_code = form.plan_code;
    }

    if (form.plan_start_date !== initialForm.plan_start_date) {
      payload.plan_start_date = form.plan_start_date.trim() === '' ? 'none' : form.plan_start_date;
    }

    editIssuer(password.text, payload)
      .then((response) => {
        if (response?.applied === true) {
          handleModalClose();

          toast({
            title: '機構已更新',
            status: 'success',
            duration: 5000,
            isClosable: true,
          });

          history.goBack();
        } else {
          setPassword((prev) => ({
            ...prev,
            isError: true,
            errorMsg: response?.error_msg || '密碼錯誤',
          }));
        }
      })
      .catch(() => {
        setPassword((prev) => ({
          ...prev,
          isError: true,
          errorMsg: '發生錯誤，請稍後再試',
        }));
      });
  };

  return (
    <Flex w="80vw" mx="auto" pt="50px" pb="100px" justify="center" alignItems="flex-start">
      <Flex maxW="679px" w="100%" direction="column">
        <Box>
          <Text fontWeight="700" fontSize="30px" lineHeight="133%" color="#000000" textAlign="center">
            編輯機構
          </Text>
        </Box>

        <Stack spacing="50px" mt="50px">
          {/* 機構代碼 */}
          <Flex gap="30px">
            <Text {...LABEL_STYLE}>機構代碼</Text>

            <Box flex="1" px="16px" py="10px" borderRadius="6px" border="1px solid #A8B8C2" bg="#F5F8FA" minH="42px">
              <Text fontFamily="'Open Sans', sans-serif" fontSize="16px" color="#4F6470">
                {issuer.id}
              </Text>
            </Box>
          </Flex>

          {/* 名稱 */}
          <Flex gap="30px">
            <Text {...LABEL_STYLE}>名稱</Text>

            <Box flex="1" px="16px" py="10px" borderRadius="6px" border="1px solid #A8B8C2" bg="#F5F8FA" minH="42px">
              <Text fontFamily="'Open Sans', sans-serif" fontSize="16px" color="#4F6470">
                {issuer.name}
              </Text>
            </Box>
          </Flex>

          {/* 縮寫 */}
          <Flex gap="30px">
            <Text {...LABEL_STYLE}>縮寫</Text>

            <Box flex="1" px="16px" py="10px" borderRadius="6px" border="1px solid #A8B8C2" bg="#F5F8FA" minH="42px">
              <Text fontFamily="'Open Sans', sans-serif" fontSize="16px" color="#4F6470">
                {issuer.acronym}
              </Text>
            </Box>
          </Flex>

          {/* 證書名稱 */}
          <Flex gap="30px">
            <Text {...LABEL_STYLE}>證書名稱</Text>

            <Box flex="1" px="16px" py="10px" borderRadius="6px" border="1px solid #A8B8C2" bg="#F5F8FA" minH="42px">
              <Text fontFamily="'Open Sans', sans-serif" fontSize="16px" color="#4F6470">
                {issuer.cert_name}
              </Text>
            </Box>
          </Flex>

          {/* 目前 IP */}
          <Flex gap="30px">
            <Text {...LABEL_STYLE}>目前 IP</Text>

            <Box flex="1" px="16px" py="10px" borderRadius="6px" border="1px solid #A8B8C2" bg="#F5F8FA" minH="42px">
              <Text fontFamily="'Open Sans', sans-serif" fontSize="16px" color="#4F6470">
                {issuer.ips.length > 0 ? issuer.ips.join(', ') : '尚無 IP'}
              </Text>
            </Box>
          </Flex>

          {/* 新增 IP */}
          <Flex gap="30px">
            <Text {...LABEL_STYLE}>新增 IP</Text>

            <Flex direction="column" w="100%">
              <FormControl isInvalid={!!ipError}>
                <Input
                  {...INPUT_STYLE}
                  value={form.ips}
                  onChange={(e) => handleIpsChange(e.target.value)}
                  placeholder="例如：1.2.3.4, 5.6.7.8"
                />

                {!ipError && (
                  <Text fontFamily="'Open Sans', sans-serif" fontSize="13px" color="#4F6470" mt="6px">
                    多個 IP 請以逗號分隔
                  </Text>
                )}

                <FormErrorMessage>{ipError}</FormErrorMessage>
              </FormControl>
            </Flex>
          </Flex>

          {/* 方案 */}
          <Flex gap="30px">
            <Text {...LABEL_STYLE}>方案</Text>

            <Select
              value={form.plan_code}
              onChange={(e) =>
                setForm((p) => ({
                  ...p,
                  plan_code: e.target.value,
                }))
              }
              fontFamily="'Open Sans', sans-serif"
              fontSize="16px"
              color="#00292B"
              borderColor="#A8B8C2"
              borderRadius="6px"
              _focus={{
                borderColor: '#00AAFF',
                boxShadow: '0 0 0 1px #00AAFF',
              }}
            >
              {plans.map((plan) => (
                <option key={plan.code} value={plan.code}>
                  {plan.name_zh}
                </option>
              ))}
            </Select>
          </Flex>

          {/* 起始日期 */}
          <Flex gap="30px">
            <Text {...LABEL_STYLE}>起始日期</Text>

            <Flex direction="column" w="100%">
              <FormControl isInvalid={!!planStartDateError}>
                <Input
                  {...INPUT_STYLE}
                  type="date"
                  min={todayLocal()}
                  value={form.plan_start_date}
                  isDisabled={planStarted}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      plan_start_date: e.target.value,
                    }))
                  }
                />

                {!planStartDateError && (
                  <Text fontFamily="'Open Sans', sans-serif" fontSize="13px" color="#4F6470" mt="6px">
                    {planStarted ? '方案已開始，無法修改起始日期' : '可保留未指定，不修改此欄位'}
                  </Text>
                )}

                <FormErrorMessage>{planStartDateError}</FormErrorMessage>
              </FormControl>
            </Flex>
          </Flex>
        </Stack>

        <Flex mt="60px" justify="center">
          <Button
            p="8px 12px"
            onClick={openPasswordModal}
            isDisabled={!isDirty || !isSubmittable}
            _disabled={{
              opacity: 0.4,
              cursor: 'not-allowed',
              pointerEvents: 'auto',
            }}
            _hover={{
              opacity: 0.85,
            }}
          >
            更新
          </Button>
        </Flex>
      </Flex>

      <Modal isOpen={isOpen} onClose={handleModalClose} size="xl" isCentered>
        <ModalOverlay />

        <ModalContent maxW="540px">
          <ModalHeader p="24px 24px 16px 24px">
            <Text textStyle="semibold_lg" color="text.main">
              請輸入密碼進行更新
            </Text>
          </ModalHeader>

          <ModalBody p="0 24px 16px 24px" color="grey.700">
            <FormControl w="80%" isInvalid={password.isError}>
              <InputGroup>
                <InputLeftElement color="text.main">
                  <Text textStyle="semibold_sm">密碼</Text>
                </InputLeftElement>

                <Input
                  type={password.visible ? 'text' : 'password'}
                  placeholder="請輸入密碼"
                  value={password.text}
                  onChange={(e) =>
                    setPassword((prev) => ({
                      ...prev,
                      text: e.target.value,
                    }))
                  }
                />

                <InputRightElement
                  cursor="pointer"
                  onClick={() =>
                    setPassword((prev) => ({
                      ...prev,
                      visible: !prev.visible,
                    }))
                  }
                >
                  {password.visible ? <ViewOffIcon color="primary.main" /> : <ViewIcon color="primary.main" />}
                </InputRightElement>
              </InputGroup>

              <FormErrorMessage>{password.errorMsg}</FormErrorMessage>
            </FormControl>
          </ModalBody>

          <ModalFooter p="0 24px 24px 24px" gap="8px">
            <Button variant="secondary" onClick={handleModalClose}>
              返回
            </Button>

            <Button variant="primary" onClick={handleUpdate} isDisabled={!password.text}>
              更新
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
};

export default EditIssuerPage;
